import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  fetchUsers,
  updateUser,
  deleteUser,
  editProfile,
  adminResetPassword,
  fetchLevels,
  fetchEmployeeTypes,
  fetchUserRoles,
  fetchDepartments,
  fetchDottedReportees,
  setDottedReportees,
} from '../services/api';
import Autocomplete from '@mui/material/Autocomplete';

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [employeeTypes, setEmployeeTypes] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [editForm, setEditForm] = useState({});
  const [profileForm, setProfileForm] = useState({});
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [editDottedIds, setEditDottedIds] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, levelsData, employeeTypesData, userRolesData, departmentsData, allUsersData] = await Promise.all([
        fetchUsers(),
        fetchLevels(),
        fetchEmployeeTypes(),
        fetchUserRoles(),
        fetchDepartments(),
        fetchUsers(),
      ]);
      setUsers(usersData);
      setLevels(levelsData);
      setEmployeeTypes(employeeTypesData);
      setUserRoles(userRolesData);
      setDepartments(departmentsData);
      setAllUsers(allUsersData);
    } catch (error) {
      setError('Failed to load data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (user) => {
    setSelectedUser(user);
    setEditForm({
      full_name: user.full_name,
      email: user.email,
      contact: user.contact,
      level_id: user.level_id,
      employee_type_id: user.employee_type_id,
      user_role_id: user.user_role_id,
      work_location: user.work_location,
      department_id: user.department_id || '',
      direct_reportee_id: user.direct_reportee_id || '',
    });
    // Fetch dotted-line reportees
    try {
      const dotted = await fetchDottedReportees(user.id);
      setEditDottedIds(dotted.map(d => d.dotted_reportee_id));
    } catch {
      setEditDottedIds([]);
    }
    setEditDialogOpen(true);
  };

  const handleEditDottedChange = (event, value) => {
    setEditDottedIds(value.map(u => u.id));
  };

  const handleEditSubmit = async () => {
    try {
      const updateData = {
        ...editForm,
        department_id: editForm.department_id ? parseInt(editForm.department_id) : null,
        direct_reportee_id: editForm.direct_reportee_id ? parseInt(editForm.direct_reportee_id) : null,
      };
      await updateUser(selectedUser.id, updateData);
      await setDottedReportees(selectedUser.id, editDottedIds);
      setSuccess('User updated successfully!');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      setError(error.message);
    }
  };

  const handleProfileEdit = (user) => {
    setSelectedUser(user);
    setProfileForm({
      full_name: user.full_name,
      contact: user.contact,
    });
    setProfileDialogOpen(true);
  };

  const handleProfileSubmit = async () => {
    try {
      await editProfile(selectedUser.id, profileForm);
      setSuccess('Profile updated successfully!');
      setProfileDialogOpen(false);
      loadData();
    } catch (error) {
      setError(error.message);
    }
  };

  const handlePasswordReset = (user) => {
    setSelectedUser(user);
    setPasswordForm({
      new_password: '',
      confirm_password: '',
    });
    setPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    try {
      await adminResetPassword(selectedUser.id, { new_password: passwordForm.new_password });
      setSuccess('Password reset successfully!');
      setPasswordDialogOpen(false);
      setPasswordForm({ new_password: '', confirm_password: '' });
    } catch (error) {
      setError(error.message);
    }
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteUser(selectedUser.id);
      setSuccess('User deleted successfully!');
      setDeleteDialogOpen(false);
      loadData();
    } catch (error) {
      setError(error.message);
    }
  };

  const getLevelName = (levelId) => {
    const level = levels.find(l => l.id === levelId);
    return level ? level.level_name : 'N/A';
  };

  const getEmployeeTypeName = (typeId) => {
    const type = employeeTypes.find(t => t.id === typeId);
    return type ? type.name : 'N/A';
  };

  const getUserRoleName = (roleId) => {
    const role = userRoles.find(r => r.id === roleId);
    return role ? role.name : 'N/A';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/add')}
          >
            Add New User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contact</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Level</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Employee Type</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>User Role</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Work Location</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Department</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Direct Reportee</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Dotted-line Reportees</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {user.full_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{user.contact}</TableCell>
                  <TableCell>
                    <Chip label={getLevelName(user.level_id)} color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={getEmployeeTypeName(user.employee_type_id)} color="secondary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={getUserRoleName(user.user_role_id)} color="success" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={user.work_location || 'N/A'} color={user.work_location === 'Offshore' ? 'warning' : 'info'} variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={user.department_name || 'N/A'} color="default" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {user.direct_reportee_name || '—'}
                  </TableCell>
                  <TableCell>
                    {user.dotted_reportee_ids && user.dotted_reportee_ids.length > 0
                      ? allUsers
                          .filter(u => user.dotted_reportee_ids.includes(u.id))
                          .map(u => u.full_name)
                          .join(', ')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit User">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(user)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Profile">
                        <IconButton
                          size="small"
                          color="secondary"
                          onClick={() => handleProfileEdit(user)}
                        >
                          <PersonIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handlePasswordReset(user)}
                        >
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(user)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={editForm.full_name || ''}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={editForm.email || ''}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact"
                  value={editForm.contact || ''}
                  onChange={(e) => setEditForm({ ...editForm, contact: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={editForm.level_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, level_id: e.target.value })}
                    label="Level"
                  >
                    {levels.map((level) => (
                      <MenuItem key={level.id} value={level.id}>
                        {level.level_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Employee Type</InputLabel>
                  <Select
                    value={editForm.employee_type_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, employee_type_id: e.target.value })}
                    label="Employee Type"
                  >
                    {employeeTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>User Role</InputLabel>
                  <Select
                    value={editForm.user_role_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, user_role_id: e.target.value })}
                    label="User Role"
                  >
                    {userRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Work Location</InputLabel>
                  <Select
                    value={editForm.work_location || ''}
                    onChange={(e) => setEditForm({ ...editForm, work_location: e.target.value })}
                    label="Work Location"
                  >
                    <MenuItem value="Onsite">Onsite</MenuItem>
                    <MenuItem value="Offshore">Offshore</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={editForm.department_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, department_id: e.target.value })}
                    label="Department"
                  >
                    {departments.map((dept) => (
                      <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Direct Reportee</InputLabel>
                  <Select
                    value={editForm.direct_reportee_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, direct_reportee_id: e.target.value })}
                    label="Direct Reportee"
                  >
                    <MenuItem value="">None</MenuItem>
                    {allUsers.filter(u => u.id !== selectedUser?.id).map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.full_name} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={allUsers.filter(u => u.id !== selectedUser?.id && u.id !== parseInt(editForm.direct_reportee_id))}
                  getOptionLabel={(option) => `${option.full_name} (${option.email})`}
                  value={allUsers.filter(u => editDottedIds.includes(u.id))}
                  onChange={handleEditDottedChange}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" label="Dotted-line Reportees" placeholder="Select users" />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Profile</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={profileForm.full_name || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Contact"
                  value={profileForm.contact || ''}
                  onChange={(e) => setProfileForm({ ...profileForm, contact: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleProfileSubmit} variant="contained">Update Profile</Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button onClick={handlePasswordSubmit} variant="contained">Reset Password</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.full_name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserList; 